import React, { KeyboardEvent, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { Fact } from "./facts";

interface FactModalProps {
  fact: Fact;
  count: number;
  total: number;
  isOpen: boolean;
  onClose: () => void;
  onNext: () => void;
}

const FactModal = ({
  fact,
  count,
  total,
  isOpen,
  onClose,
  onNext,
}: FactModalProps) => {
  const nextButtonRef = useRef<HTMLButtonElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen) nextButtonRef.current?.focus();
  }, [isOpen]);

  useEffect(() => {
    const closeOnEscape = (event: globalThis.KeyboardEvent) => {
      if (isOpen && event.key === "Escape") onClose();
    };

    document.addEventListener("keydown", closeOnEscape);
    return () => document.removeEventListener("keydown", closeOnEscape);
  }, [isOpen, onClose]);

  const trapFocus = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== "Tab") return;

    const buttons = modalRef.current?.querySelectorAll("button");
    if (!buttons?.length) return;

    const first = buttons[0];
    const last = buttons[buttons.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  return createPortal(
    <div
      className="hero__fact-modal"
      id="hero-fact-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="hero-fact-title"
      aria-hidden={!isOpen}
      onKeyDown={trapFocus}
      ref={modalRef}
    >
      <button
        className="hero__fact-backdrop"
        type="button"
        aria-label="Close"
        onClick={onClose}
      />
      <div className="hero__fact-card">
        <button
          className="hero__fact-close"
          type="button"
          aria-label="Close"
          onClick={onClose}
        >
          &times;
        </button>
        <div className="hero__fact-top-row">
          <span className="hero__fact-chip" data-category={fact.category}>
            {fact.category}
          </span>
          <span className="hero__fact-count">
            Fact {count} of {total}
          </span>
        </div>
        <h2 className="hero__fact-title" id="hero-fact-title">
          {fact.title}
        </h2>
        <p
          className="hero__fact-body"
          dangerouslySetInnerHTML={{ __html: fact.body }}
        />
        <div className="hero__fact-actions">
          <button
            className="hero__fact-button hero__fact-button--primary"
            type="button"
            onClick={onNext}
            ref={nextButtonRef}
          >
            Show me another &rarr;
          </button>
          <button
            className="hero__fact-button hero__fact-button--secondary"
            type="button"
            onClick={onClose}
          >
            Close
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default FactModal;
